import { CssBaseline, createTheme, StyledEngineProvider, ThemeProvider } from "@mui/material";
import { type Shadows } from "@mui/material/styles";
import React from "react";

/**
 * Types
 */
interface Props {
  children: React.ReactElement[] | React.ReactElement;
}

/**
 * Brand tokens
 *
 * Mirrors the Hey Lady! Member Tools palette (frontend/style.css) so the admin
 * and the member tools read as one product.
 */
export const brand = {
  green: "#00583D",
  greenDeep: "#267059",
  greenSoft: "#69A794",
  greenTint: "#d1e1de",
  greenWash: "#f4faf8",
  orange: "#D36A19",
  orangeDeep: "#b9560f",
  orangeSoft: "#e6a97a",
  pink: "#F2B6CF",
  pinkWash: "#fff0f5",
  cream: "#F0DACD",
  creamWash: "#fefdfb",
  ink: "#272727",
  grey: "#5c5c5c",
  line: "#e7e2da",
  inputLine: "#ddd6cc",
  okBg: "#e7f1ec",
  okFg: "#287761",
  offBg: "#fbe7ef",
  offFg: "#b23a6a",
} as const;

/** The brand strip: green → soft → cream → orange → pink */
export const brandGradient = `linear-gradient(90deg, ${brand.green} 0%, ${brand.greenSoft} 28%, ${brand.cream} 52%, ${brand.orange} 78%, ${brand.pink} 100%)`;

/**
 * Palette
 */
const palette = {
  mode: "light" as const,
  primary: {
    main: brand.green,
    light: brand.greenSoft,
    dark: "#00402c",
    contrastText: "#ffffff",
  },
  secondary: {
    main: brand.orange,
    light: brand.orangeSoft,
    dark: brand.orangeDeep,
    contrastText: "#ffffff",
  },
  success: {
    main: brand.okFg,
    light: brand.okBg,
    contrastText: "#ffffff",
  },
  warning: {
    main: brand.orange,
    contrastText: "#ffffff",
  },
  error: {
    main: brand.offFg,
    light: brand.offBg,
    contrastText: "#ffffff",
  },
  background: {
    default: brand.creamWash,
    paper: "#ffffff",
  },
  text: {
    primary: brand.ink,
    secondary: brand.grey,
  },
  divider: brand.line,
};

/**
 * Shadows
 *
 * Soft and green-tinted, matching the member tools' cards (0 4px 16px) and
 * header (0 10px 30px) rather than MUI's default hard black.
 */
const softShadow = (y: number, blur: number, alpha: number) =>
  `0 ${y}px ${blur}px rgba(0, 88, 61, ${alpha})`;

const shadows: Shadows = [
  "none",
  softShadow(1, 2, 0.04),
  softShadow(2, 6, 0.05),
  "0 4px 16px rgba(39, 39, 39, 0.04)",
  softShadow(5, 18, 0.06),
  softShadow(10, 30, 0.08),
  softShadow(11, 32, 0.08),
  softShadow(12, 34, 0.09),
  softShadow(13, 36, 0.09),
  softShadow(14, 38, 0.1),
  softShadow(15, 40, 0.1),
  softShadow(16, 42, 0.1),
  softShadow(17, 44, 0.11),
  softShadow(18, 46, 0.11),
  softShadow(19, 48, 0.11),
  softShadow(20, 50, 0.12),
  softShadow(21, 52, 0.12),
  softShadow(22, 54, 0.12),
  softShadow(23, 56, 0.13),
  softShadow(24, 58, 0.13),
  softShadow(25, 60, 0.13),
  softShadow(26, 62, 0.14),
  softShadow(27, 64, 0.14),
  softShadow(28, 66, 0.14),
  softShadow(30, 70, 0.15),
];

/**
 * Typography
 *
 * Gibson ships Light/Regular/SemiBold only — bold maps to 600 so the browser
 * never synthesises a heavier face.
 */
const fontFamily = [
  '"Gibson"',
  "-apple-system",
  '"Segoe UI"',
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
].join(",");

const heading = { fontWeight: 600, color: brand.green, letterSpacing: "-0.01em" };

const typography = {
  fontFamily,
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 600,
  h1: { ...heading, fontSize: 34, letterSpacing: "-0.02em" },
  h2: { ...heading, fontSize: 28, letterSpacing: "-0.02em" },
  h3: { ...heading, fontSize: 23 },
  h4: { ...heading, fontSize: 19 },
  h5: { ...heading, fontSize: 16 },
  h6: { ...heading, fontSize: 14 },
  button: { fontWeight: 600, letterSpacing: 0 },
  overline: { fontWeight: 600, letterSpacing: "0.07em" },
};

/**
 * Theme
 */
const theme = createTheme({
  palette,
  shadows,
  typography,
  shape: { borderRadius: 12 },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: "Gibson";
          font-weight: 300;
          font-style: normal;
          font-display: swap;
          src: url("/assets/fonts/GibsonLight.otf") format("opentype");
        }
        @font-face {
          font-family: "Gibson";
          font-weight: 300;
          font-style: italic;
          font-display: swap;
          src: url("/assets/fonts/GibsonLightItalic.otf") format("opentype");
        }
        @font-face {
          font-family: "Gibson";
          font-weight: 400;
          font-style: normal;
          font-display: swap;
          src: url("/assets/fonts/GibsonRegular.otf") format("opentype");
        }
        @font-face {
          font-family: "Gibson";
          font-weight: 400;
          font-style: italic;
          font-display: swap;
          src: url("/assets/fonts/GibsonItalic.otf") format("opentype");
        }
        @font-face {
          font-family: "Gibson";
          font-weight: 600;
          font-style: normal;
          font-display: swap;
          src: url("/assets/fonts/GibsonSemiBold.otf") format("opentype");
        }
        @font-face {
          font-family: "Gibson";
          font-weight: 600;
          font-style: italic;
          font-display: swap;
          src: url("/assets/fonts/GibsonSemiBdItalic.otf") format("opentype");
        }
        body {
          background-color: ${brand.creamWash};
          background-image:
            radial-gradient(1200px 500px at 100% -10%, ${brand.greenWash} 0%, transparent 60%),
            radial-gradient(1000px 500px at -10% 0%, ${brand.pinkWash} 0%, transparent 55%);
          background-attachment: fixed;
        }
      `,
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.92rem",
          padding: "0.62rem 1.25rem",
          transition: "background .15s, transform .05s",
          "&:active": { transform: "translateY(1px)" },
        },
        containedPrimary: {
          "&:hover": { backgroundColor: brand.greenDeep },
        },
        containedSecondary: {
          "&:hover": { backgroundColor: brand.orangeDeep },
        },
        outlined: {
          backgroundColor: brand.greenWash,
          borderColor: brand.greenTint,
          "&:hover": { backgroundColor: brand.greenTint, borderColor: brand.greenTint },
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 16 },
        outlined: { borderColor: brand.line },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 3 },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: `1px solid ${brand.line}`,
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 11,
          backgroundColor: "#ffffff",
          "& .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1.5,
            borderColor: brand.inputLine,
          },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: brand.greenSoft },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px rgba(105, 167, 148, 0.25)`,
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: 1.5,
              borderColor: brand.greenSoft,
            },
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 600,
          fontSize: "0.76rem",
        },
        colorSuccess: { backgroundColor: brand.okBg, color: brand.okFg },
        colorError: { backgroundColor: brand.offBg, color: brand.offFg },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: { color: brand.greenSoft },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 600,
            color: brand.grey,
            fontSize: "0.78rem",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            backgroundColor: brand.greenWash,
          },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: brand.ink,
          fontSize: "0.78rem",
          fontWeight: 400,
          borderRadius: 8,
          padding: "6px 10px",
        },
      },
    },
  },
});

const Theme: React.FC<Props> = ({ children }: Props) => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default Theme;
